import { isBlurhashValid } from "blurhash";
import { z } from "zod";

import { dateSchema } from "./utils";

const blurhashSchema = z.string().superRefine((value, ctx) => {
  const r = isBlurhashValid(value);

  if (!r.result) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: r.errorReason,
    });
  }
});

const baseAttachmentSchema = z.object({
  blurhash: blurhashSchema.optional().catch(undefined),
  description: z.string().catch(""),
  external_video_id: z.string().optional().catch(undefined), // TruthSocial
  id: z.string(),
  pleroma: z
    .object({
      mime_type: z.string().regex(/^\w+\/[-+.\w]+$/),
    })
    .optional()
    .catch(undefined),
  preview_url: z.string().url().catch(""),
  remote_url: z.string().url().nullable().catch(null),
  sensitive: z.coerce.boolean(),
  type: z.string(),
  url: z.string().url(),
});

const imageMetaSchema = z
  .object({
    width: z.number(),
    height: z.number(),
    aspect: z.number().optional().catch(undefined),
    duration: z.number().optional().catch(undefined),
  })
  .transform((meta) => ({
    ...meta,
    aspect:
      typeof meta.aspect === "number" ? meta.aspect : meta.width / meta.height,
  }));

const imageAttachmentSchema = baseAttachmentSchema.extend({
  type: z.literal("image"),
  meta: z
    .object({
      original: imageMetaSchema.optional().catch(undefined),
    })
    .catch({}),
  width: z.number().optional().catch(undefined),
  height: z.number().optional().catch(undefined),
});

const videoAttachmentSchema = baseAttachmentSchema.extend({
  type: z.literal("video"),
  meta: z
    .object({
      duration: z.number().optional().catch(undefined),
      original: imageMetaSchema.optional().catch(undefined),
      small: imageMetaSchema.optional().catch(undefined),
    })
    .catch({}),
});

const gifvAttachmentSchema = baseAttachmentSchema.extend({
  type: z.literal("gifv"),
  meta: z
    .object({
      duration: z.number().optional().catch(undefined),
      original: imageMetaSchema.optional().catch(undefined),
    })
    .catch({}),
});

const tvAttachmentSchema = baseAttachmentSchema.extend({
  type: z.literal("tv"),
  text_url: z.string().nullable(),
  tv: z.object({
    channel_id: z.number(),
    end_time: dateSchema,
    name: z.string(),
    pltv_timespan: z.number(),
    start_time: dateSchema,
  }),
  meta: z.object({
    original: z.object({
      width: z.number(),
      height: z.number(),
      duration: z.number(),
    }),
  }),
  preview_remote_url: z.string().url().nullable(),
});

const audioAttachmentSchema = baseAttachmentSchema.extend({
  type: z.literal("audio"),
  meta: z
    .object({
      duration: z.number().optional().catch(undefined),
      original: z
        .object({
          duration: z.number().optional().catch(undefined),
        })
        .optional()
        .catch(undefined),
      colors: z
        .object({
          background: z.string().optional().catch(undefined),
          foreground: z.string().optional().catch(undefined),
          accent: z.string().optional().catch(undefined),
          duration: z.number().optional().catch(undefined),
        })
        .optional()
        .catch(undefined),
    })
    .catch({}),
});

const unknownAttachmentSchema = baseAttachmentSchema.extend({
  type: z.literal("unknown"),
});

/** https://docs.joinmastodon.org/entities/attachment */
const attachmentSchema = z
  .discriminatedUnion("type", [
    imageAttachmentSchema,
    videoAttachmentSchema,
    gifvAttachmentSchema,
    audioAttachmentSchema,
    tvAttachmentSchema,
    unknownAttachmentSchema,
  ])
  .transform((attachment) => {
    if (!attachment.preview_url) {
      attachment.preview_url = attachment.url;
    }

    if (attachment.type === "image") {
      attachment.width = attachment.meta.original?.width;
      attachment.height = attachment.meta.original?.height;
    }

    return attachment;
  });

type Attachment = z.infer<typeof attachmentSchema>;
type VideoAttachment = z.infer<typeof videoAttachmentSchema>;
type TvAttachment = z.infer<typeof tvAttachmentSchema>;

export {
  attachmentSchema,
  type Attachment,
  type TvAttachment,
  type VideoAttachment,
};
